import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
} from "react";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { TicketTypeSerializer } from "@shared/modules/events/types/public";

interface SubOptionsContextType {
  // State management
  isViewingSubOptions: boolean;
  hasSubOptionChildren: boolean;
  showSubOptions: () => void;
  hideSubOptions: () => void;

  // Centralized basket data
  itemGroups: TicketItemGroupType[];

  // Sub-option specific data and functions
  subOptionChildren: TicketTypeSerializer[];
  subOptionPurchaseLimits: {
    [ticketTypeId: number]: { min: number; max: number };
  };
  getFilteredTicketTypes: (
    mainTicketTypes: TicketTypeSerializer[]
  ) => TicketTypeSerializer[];
  getSubOptionPurchaseLimits: (
    ticketId: number
  ) => { min: number; max: number } | undefined;
}

const SubOptionsContext = createContext<SubOptionsContextType | undefined>(
  undefined
);

interface SubOptionsProviderProps {
  children: ReactNode;
  eventId?: number;
}

export const SubOptionsProvider: React.FC<SubOptionsProviderProps> = ({
  children,
  eventId,
}) => {
  const [isViewingSubOptions, setIsViewingSubOptions] = useState(false);

  // Get basket data from useBasketService (centralized)
  const basketService = useBasketService();
  const itemGroups = basketService?.itemGroups || [];

  // Get sub-option children from basket item groups, filtered by current event
  const subOptionChildren = useMemo(() => {
    if (!itemGroups?.length) return [];

    // If no eventId is provided, include all sub-options (backwards compatibility)
    if (!eventId) {
      const allSubOptions: TicketTypeSerializer[] = [];
      itemGroups.forEach((ig) => {
        const subOptions = (ig as TicketItemGroupType).sub_option_children_data;
        if (subOptions && subOptions.length > 0) {
          allSubOptions.push(...subOptions);
        }
      });
      return allSubOptions;
    }

    // 1. Create a lookup map for efficient parent searching.
    // This is much faster than using .find() inside a loop.
    const itemGroupMap = new Map<number, TicketItemGroupType>(
      itemGroups
        .filter((ig) => ig.id !== undefined)
        .map((ig) => [ig.id as number, ig as TicketItemGroupType])
    );

    const allSubOptions: TicketTypeSerializer[] = [];

    itemGroups.forEach((ig) => {
      const itemGroup = ig as TicketItemGroupType;
      const subOptions = itemGroup.sub_option_children_data;

      if (!subOptions || subOptions.length === 0) {
        return; // Skip if there are no sub-options
      }

      // CONDITION 1: The item group is directly related to the current event.
      const isDirectMatch = itemGroup.event === eventId;

      // CONDITION 2: The item group is a child of a package from the current event.
      let isChildOfPackage = false;
      if (itemGroup.parent) {
        const parentItemGroup = itemGroupMap.get(itemGroup.parent);
        if (parentItemGroup && parentItemGroup.event === eventId) {
          isChildOfPackage = true;
        }
      }

      // If either condition is true, add its sub-options to our list.
      if (isDirectMatch || isChildOfPackage) {
        allSubOptions.push(...subOptions);
      }
    });

    return allSubOptions;
  }, [itemGroups, eventId]);

  // Calculate purchase limits for sub-options based on parent ticket quantities
  const subOptionPurchaseLimits = useMemo(() => {
    if (!itemGroups?.length || !isViewingSubOptions) return {};

    // Create limits object for each sub-option ticket type
    const limits: { [ticketTypeId: number]: { min: number; max: number } } = {};

    // For each item group that has sub-options, set limits based on that group's quantity
    itemGroups.forEach((ig) => {
      const subOptions = (ig as TicketItemGroupType).sub_option_children_data;
      if (ig.item_type === "ticket" && subOptions && subOptions.length > 0) {
        // Set limits for each sub-option based on this parent's quantity
        subOptions.forEach((subOption) => {
          limits[subOption.id] = {
            min: ig.quantity || 1,
            max: ig.quantity || 1,
          };
        });
      }
    });

    return limits;
  }, [itemGroups, isViewingSubOptions, subOptionChildren]);

  // Auto-detect if we have sub-option children
  const hasSubOptionChildren = useMemo(() => {
    return subOptionChildren.length > 0;
  }, [subOptionChildren]);

  // Function to determine which tickets to display based on current view
  const getFilteredTicketTypes = (mainTicketTypes: TicketTypeSerializer[]) => {
    if (isViewingSubOptions) {
      return subOptionChildren;
    }
    return mainTicketTypes;
  };

  // Helper function to get sub-option purchase limits only
  const getSubOptionPurchaseLimits = (ticketId: number) => {
    if (isViewingSubOptions && subOptionPurchaseLimits[ticketId]) {
      return subOptionPurchaseLimits[ticketId];
    }
    return undefined;
  };

  const showSubOptions = () => setIsViewingSubOptions(true);
  const hideSubOptions = () => setIsViewingSubOptions(false);

  const value: SubOptionsContextType = {
    // State management
    isViewingSubOptions,
    hasSubOptionChildren,
    showSubOptions,
    hideSubOptions,

    // Centralized basket data
    itemGroups,

    // Sub-option specific data and functions
    subOptionChildren,
    subOptionPurchaseLimits,
    getFilteredTicketTypes,
    getSubOptionPurchaseLimits,
  };

  return (
    <SubOptionsContext.Provider value={value}>
      {children}
    </SubOptionsContext.Provider>
  );
};

export const useSubOptions = (): SubOptionsContextType => {
  const context = useContext(SubOptionsContext);
  if (context === undefined) {
    throw new Error("useSubOptions must be used within a SubOptionsProvider");
  }
  return context;
};

export const useSubOptionsOptional = (): SubOptionsContextType | undefined => {
  return useContext(SubOptionsContext);
};
